/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Test Data Service
 *
 * Pure functions for extracting trigger output schemas from swagger specs
 * and generating realistic sample JSON payloads for draft test runs.
 * No React, no hooks, no API calls — swagger is passed in, not fetched.
 */

// =============================================================================
// TYPES
// =============================================================================

export interface TriggerSchemaField {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  enum?: string[];
  example?: unknown;
  default?: unknown;
  format?: string;
  properties?: TriggerSchemaField[];
  items?: TriggerSchemaField; // for array types — schema of array elements
}

// Trigger types that support test data generation (API connection family)
const TEST_DATA_TRIGGER_TYPES = [
  'apiconnection',
  'apiconnectionwebhook',
  'apiconnectionnotification',
] as const;

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Returns true if the trigger type supports test data generation.
 * Only API connection triggers (polling, webhook, notification) qualify.
 */
export const canProvideTestData = (triggerType?: string): boolean => {
  if (!triggerType) return false;
  return TEST_DATA_TRIGGER_TYPES.some(
    t => t === triggerType.toLowerCase()
  );
};

/**
 * Returns true if a swagger schema uses x-ms-dynamic-schema (runtime-resolved).
 * Dynamic schemas have empty properties and require a separate API call to resolve.
 */
export const hasDynamicSchema = (
  swaggerPaths: Record<string, unknown>,
  operationId: string,
  triggerType: string,
): boolean => {
  try {
    const op = findOperation(swaggerPaths, operationId);
    if (!op) return false;
    const isWebhook = triggerType.toLowerCase().includes('webhook')
      || triggerType.toLowerCase().includes('notification');
    let schema: Record<string, unknown> | null = null;
    if (isWebhook) {
      // x-ms-notification-content contains the schema directly (has properties, type, etc.)
      const notifContent = op['x-ms-notification-content'];
      schema = notifContent?.schema ?? notifContent;
      if (!schema) {
        const pathItem = findPathItem(swaggerPaths, operationId);
        const pathNotifContent = pathItem?.['x-ms-notification-content'];
        schema = pathNotifContent?.schema ?? pathNotifContent;
      }
    }
    if (!schema) schema = op.responses?.['200']?.schema;
    return !!schema?.['x-ms-dynamic-schema'];
  } catch {
    return false;
  }
};

/**
 * Checks whether a trigger operation advertises a simulate operationId
 * via the x-ms-operation-context swagger extension.
 */
export const triggerSupportsSimulate = (
  swaggerPaths: Record<string, any>,
  operationId: string
): boolean => {
  const op = findOperation(swaggerPaths, operationId);
  if (!op) return false;
  const ctx = op['x-ms-operation-context'];
  return !!(ctx?.simulate?.operationId);
};

/**
 * Extracts the trigger output schema as a flat (or nested) array of fields.
 * - Webhook/notification triggers: uses x-ms-notification-content
 * - Polling triggers: uses the 200 response schema
 */
export const getTriggerOutputSchema = (
  swaggerPaths: Record<string, any>,
  operationId: string,
  triggerType: string,
  definitions?: Record<string, any>,
): TriggerSchemaField[] => {
  try {
    const op = findOperation(swaggerPaths, operationId);
    if (!op) return [];

    const isWebhook = triggerType.toLowerCase().includes('webhook')
      || triggerType.toLowerCase().includes('notification');

    let schema: any = null;

    if (isWebhook) {
      // x-ms-notification-content can be on the operation OR the parent path item
      const notificationContent = op['x-ms-notification-content'];
      schema = notificationContent?.schema ?? notificationContent;
      if (!schema) {
        const pathItem = findPathItem(swaggerPaths, operationId);
        const pathNotificationContent = pathItem?.['x-ms-notification-content'];
        schema = pathNotificationContent?.schema ?? pathNotificationContent;
      }
      if (!schema) {
        schema = op.responses?.['200']?.schema;
      }
    } else {
      schema = op.responses?.['200']?.schema;
    }

    if (!schema) return [];
    const resolved = resolveRef(schema, definitions);
    return schemaToFields(resolved, definitions);
  } catch {
    return [];
  }
};

/**
 * Generates a sample JSON object from a schema field array.
 * Priority: field.example > field.default > field.enum[0] > heuristic > type default.
 */
export const generateSampleFromSchema = (
  fields: TriggerSchemaField[]
): Record<string, unknown> => {
  if (!fields || fields.length === 0) return {};
  return fieldsToSample(fields, 0);
};

/** Result of building a Test-draft sample for a Request/HTTP trigger schema. */
export interface RequestTriggerSample {
  /** The generated sample payload (an object or array). */
  sample: unknown;
  /** True when the schema describes content worth pre-filling the editor with. */
  hasContent: boolean;
  /** Stable hash of the schema, persisted for staleness detection. */
  hash: string;
}

/**
 * Builds a Test-draft sample for a Request/HTTP trigger's `inputs.schema`
 * (whose top level is always an object or an array).
 *
 * The presence of extracted fields is the sole content signal: an object with
 * properties or an array with items yields fields, while an empty/type-less
 * schema yields none and falls back to a blank body. The sample preserves the
 * top-level shape — an object schema produces an object, an array schema a
 * top-level array — so the prefill decision and the sample never disagree.
 */
export const buildRequestTriggerSample = (
  schema: Record<string, any> | null | undefined,
): RequestTriggerSample => {
  const empty: RequestTriggerSample = { sample: {}, hasContent: false, hash: '0' };
  if (!schema || typeof schema !== 'object') return empty;
  try {
    const fields = schemaToFields(schema);
    if (fields.length === 0) return empty;
    return {
      sample: sampleFromSchemaNode(schema, 0),
      hasContent: true,
      hash: computeSchemaHash(fields),
    };
  } catch {
    return empty;
  }
};

/**
 * Generates a single sample value for a property given its name and schema info.
 * Exposed for direct use and testing.
 */
export const generateSampleValue = (
  name: string,
  schema: {
    type?: string;
    format?: string;
    enum?: string[];
    example?: unknown;
    default?: unknown;
  }
): unknown => {
  // Priority chain: example > default > enum[0] > heuristic > type default
  if (schema.example !== undefined) return schema.example;
  if (schema.default !== undefined) return schema.default;
  if (schema.enum && schema.enum.length > 0) return schema.enum[0];

  const heuristic = heuristicByName(name, schema.format);
  if (heuristic !== undefined) return heuristic;

  return typeDefault(schema.type, schema.format);
};

/**
 * Computes a simple string hash of a schema field array for staleness detection.
 * Deterministic and order-insensitive for fields with the same content.
 */
export const computeSchemaHash = (fields: TriggerSchemaField[]): string => {
  if (!fields || fields.length === 0) return '0';

  function fieldSignature(f: TriggerSchemaField): object {
    return {
      n: f.name,
      t: f.type,
      r: f.required,
      e: f.enum,
      f: f.format,
      p: f.properties
        ? [...f.properties].sort((a, b) => a.name.localeCompare(b.name)).map(fieldSignature)
        : undefined,
      i: f.items ? fieldSignature(f.items) : undefined,
    };
  }

  const sorted = [...fields].sort((a, b) => a.name.localeCompare(b.name));
  const str = JSON.stringify(sorted.map(fieldSignature));
  return simpleHash(str);
};

/**
 * Wraps test data into a trigger invocation payload with appropriate headers.
 */
export const buildTriggerPayload = (
  testData: unknown,
  triggerName: string
): { headers: Record<string, string>; body: unknown } => {
  return {
    headers: {
      'Content-Type': 'application/json',
      'x-ms-trigger-name': triggerName,
    },
    body: testData ?? {},
  };
};

// =============================================================================
// INTERNALS
// =============================================================================

const MAX_DEPTH = 5;

/** Resolve a $ref to its definition. Returns the original schema if no $ref. */
function resolveRef(schema: any, definitions?: Record<string, any>, visited?: Set<string>): any {
  if (!schema || typeof schema !== 'object') return schema;
  if (!schema['$ref'] || !definitions) return schema;
  const refPath = schema['$ref'];
  const match = typeof refPath === 'string' ? refPath.match(/^#\/definitions\/(.+)$/) : null;
  if (!match?.[1]) return schema;
  // Guard against circular references
  const seen = visited ?? new Set<string>();
  if (seen.has(match[1])) return schema;
  seen.add(match[1]);
  const resolved = definitions[match[1]] ?? schema;
  // Recursively resolve if the target also has a $ref
  return resolveRef(resolved, definitions, seen);
}

/** Locate a swagger operation by operationId across all paths and methods. */
function findOperation(
  swaggerPaths: Record<string, any>,
  operationId: string
): any | null {
  if (!swaggerPaths || !operationId) return null;
  const methods = ['get', 'post', 'put', 'patch', 'delete', 'options', 'head'];
  for (const pathItem of Object.values(swaggerPaths)) {
    if (!pathItem || typeof pathItem !== 'object') continue;
    for (const method of methods) {
      const op = pathItem[method];
      if (op && op.operationId === operationId) return op;
    }
  }
  return null;
}

/** Locate the parent path item containing an operation with the given operationId. */
function findPathItem(
  swaggerPaths: Record<string, any>,
  operationId: string
): any | null {
  if (!swaggerPaths || !operationId) return null;
  const methods = ['get', 'post', 'put', 'patch', 'delete', 'options', 'head'];
  for (const pathItem of Object.values(swaggerPaths)) {
    if (!pathItem || typeof pathItem !== 'object') continue;
    for (const method of methods) {
      const op = pathItem[method];
      if (op && op.operationId === operationId) return pathItem;
    }
  }
  return null;
}

/** Convert a swagger schema object into TriggerSchemaField[]. */
function schemaToFields(schema: any, definitions?: Record<string, any>): TriggerSchemaField[] {
  if (!schema || typeof schema !== 'object') return [];

  // If schema has properties, enumerate them
  if (schema.properties && typeof schema.properties === 'object') {
    const requiredSet = new Set<string>(
      Array.isArray(schema.required) ? schema.required : []
    );
    return Object.entries(schema.properties).map(([name, rawProp]: [string, any]) => {
      const prop = resolveRef(rawProp, definitions);
      // Infer type when missing: presence of `properties` implies object, `items` implies array
      const inferredType = prop?.type ?? (prop?.properties ? 'object' : prop?.items ? 'array' : 'string');
      const field: TriggerSchemaField = {
        name,
        type: inferredType,
        required: requiredSet.has(name),
        description: prop?.description ?? prop?.['x-ms-summary'],
        enum: prop?.enum,
        example: prop?.example,
        default: prop?.default,
        format: prop?.format,
      };

      // Nested object (or $ref to object) — also handle implicit objects (properties present, type omitted)
      const resolved = prop?.['$ref'] ? resolveRef(prop, definitions) : prop;
      if (resolved?.properties) {
        field.properties = schemaToFields(resolved, definitions);
      } else if (prop?.properties) {
        field.properties = schemaToFields(prop, definitions);
      }

      // Array items
      if (inferredType === 'array' && prop?.items) {
        const resolvedItems = resolveRef(prop.items, definitions);
        const itemField: TriggerSchemaField = {
          name: 'item',
          type: resolvedItems.type ?? 'object',
          required: false,
        };
        if (resolvedItems.description) itemField.description = resolvedItems.description;
        if (resolvedItems.enum) itemField.enum = resolvedItems.enum;
        if (resolvedItems.example !== undefined) itemField.example = resolvedItems.example;
        if (resolvedItems.default !== undefined) itemField.default = resolvedItems.default;
        if (resolvedItems.format) itemField.format = resolvedItems.format;
        if (resolvedItems.properties) itemField.properties = schemaToFields(resolvedItems, definitions);
        field.items = itemField;
      }

      return field;
    });
  }

  // If schema is an array type at root level, represent as a single field
  if (schema.type === 'array' && schema.items) {
    const resolvedItems = resolveRef(schema.items, definitions);
    const itemField: TriggerSchemaField = {
      name: 'item',
      type: resolvedItems.type ?? 'object',
      required: false,
    };
    if (resolvedItems.description) itemField.description = resolvedItems.description;
    if (resolvedItems.enum) itemField.enum = resolvedItems.enum;
    if (resolvedItems.example !== undefined) itemField.example = resolvedItems.example;
    if (resolvedItems.default !== undefined) itemField.default = resolvedItems.default;
    if (resolvedItems.format) itemField.format = resolvedItems.format;
    if (resolvedItems.properties) itemField.properties = schemaToFields(resolvedItems, definitions);
    const rootField: TriggerSchemaField = {
      name: 'items',
      type: 'array',
      required: false,
      items: itemField,
    };
    if (schema.description) rootField.description = schema.description;
    return [rootField];
  }

  return [];
}

/**
 * Builds a sample value for a JSON schema node, preserving its shape. Used for
 * the top-level Request schema (always an object or array) and, recursively,
 * for a top-level array's items (objects or scalars). Object interiors are
 * delegated to the shared schemaToFields + fieldsToSample machinery.
 */
function sampleFromSchemaNode(schema: any, depth: number): unknown {
  if (depth >= MAX_DEPTH || !schema || typeof schema !== 'object') return {};

  const inferredType = schema.type
    ?? (schema.properties ? 'object' : schema.items ? 'array' : undefined);

  if (inferredType === 'array') {
    return schema.items ? [sampleFromSchemaNode(schema.items, depth + 1)] : [];
  }
  if (inferredType === 'object' || schema.properties) {
    return fieldsToSample(schemaToFields(schema), depth);
  }
  // Scalar schema — reached for scalar items of a top-level array.
  return generateSampleValue('', schema);
}

/** Recursively convert TriggerSchemaField[] into a sample object. */
function fieldsToSample(
  fields: TriggerSchemaField[],
  depth: number
): Record<string, unknown> {
  if (depth >= MAX_DEPTH) return {};
  const result: Record<string, unknown> = {};

  for (const field of fields) {
    if (field.type === 'object' && field.properties && field.properties.length > 0) {
      result[field.name] = fieldsToSample(field.properties, depth + 1);
    } else if (field.type === 'array' && field.items) {
      if (field.items.properties && field.items.properties.length > 0) {
        result[field.name] = [fieldsToSample(field.items.properties, depth + 1)];
      } else {
        const itemSchema: {
          type?: string;
          format?: string;
          enum?: string[];
          example?: unknown;
          default?: unknown;
        } = { type: field.items.type };
        if (field.items.format) itemSchema.format = field.items.format;
        if (field.items.enum) itemSchema.enum = field.items.enum;
        if (field.items.example !== undefined) itemSchema.example = field.items.example;
        if (field.items.default !== undefined) itemSchema.default = field.items.default;
        // Use parent field name for heuristics (e.g., "emails" → email-like value)
        result[field.name] = [generateSampleValue(field.name, itemSchema)];
      }
    } else {
      const schema: { type?: string; format?: string; enum?: string[]; example?: unknown; default?: unknown } = {};
      if (field.type) schema.type = field.type;
      if (field.format) schema.format = field.format;
      if (field.enum) schema.enum = field.enum;
      if (field.example !== undefined) schema.example = field.example;
      if (field.default !== undefined) schema.default = field.default;
      result[field.name] = generateSampleValue(field.name, schema);
    }
  }

  return result;
}

/** Name-based heuristics for generating realistic sample values. */
function heuristicByName(name: string, format?: string): unknown {
  const lower = name.toLowerCase();

  if (lower === 'email' || lower.endsWith('email') || lower.endsWith('emailaddress'))
    return 'user@contoso.com';
  if (lower === 'name' || lower === 'displayname' || lower === 'fullname')
    return 'John Doe';
  if (lower === 'firstname') return 'John';
  if (lower === 'lastname') return 'Doe';
  if (lower === 'subject') return 'Sample subject';
  if (lower === 'title') return 'Sample title';
  if (lower === 'description') return 'Sample description';
  if (lower === 'body' || lower === 'content' || lower === 'message')
    return 'Sample content';
  if (lower === 'url' || lower === 'link' || lower === 'href' || lower === 'website')
    return 'https://example.com';
  if (lower === 'phone' || lower === 'phonenumber')
    return '+1-555-0100';
  if (lower === 'address') return '123 Main St';
  if (lower === 'city') return 'Seattle';
  if (lower === 'state') return 'WA';
  if (lower === 'country') return 'US';
  if (lower === 'zipcode' || lower === 'postalcode') return '98101';

  // Date/time patterns
  if (lower.includes('date') || lower.includes('time') || lower === 'createdat'
    || lower === 'updatedat' || lower === 'timestamp') {
    if (format === 'date') return '2025-01-15';
    return '2025-01-15T10:30:00Z';
  }

  // ID patterns
  if (lower === 'id' || lower.endsWith('id') || lower.endsWith('_id'))
    return 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';

  // Count/number patterns
  if (lower === 'count' || lower === 'total' || lower === 'quantity' || lower === 'amount')
    return 1;
  if (lower === 'page' || lower === 'pagenumber') return 1;
  if (lower === 'size' || lower === 'pagesize' || lower === 'limit') return 10;
  if (lower === 'index' || lower === 'offset') return 0;
  if (lower === 'price' || lower === 'cost') return 9.99;

  // Boolean patterns
  if (lower.startsWith('is') || lower.startsWith('has') || lower.startsWith('can')
    || lower.startsWith('should') || lower === 'enabled' || lower === 'active')
    return true;

  return undefined;
}

/** Type-based default values when no heuristic matches. */
function typeDefault(type?: string, format?: string): unknown {
  switch (type) {
    case 'string':
      if (format === 'date-time') return '2025-01-15T10:30:00Z';
      if (format === 'date') return '2025-01-15';
      if (format === 'uri') return 'https://example.com';
      if (format === 'email') return 'user@contoso.com';
      if (format === 'uuid') return 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';
      if (format === 'byte') return 'c2FtcGxl';
      if (format === 'binary') return '';
      if (format === 'html') return '<p>Sample</p>';
      return 'sample';
    case 'number':
    case 'integer':
      return 0;
    case 'boolean':
      return true;
    case 'array':
      return [];
    case 'object':
      return {};
    default:
      return 'sample';
  }
}

/** Simple djb2-style string hash, returned as hex string. */
function simpleHash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16);
}
